import { useState, useSyncExternalStore } from "react";
import { useNavigate, useParams } from "react-router";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { abweisungsgrund, befehl } from "../plattform/api";
import { meldungszusatzRegistrieren, type EigeneMeldung } from "../plattform/Meldungen";
import { useDrueckjagdDesSchusses } from "./Ergaenzen";
import { useEigeneMeldungen } from "./lage";

/**
 * Eigene Schüsse in der Ansicht „Meldungen“ (oberflaeche.md §8 Nr. 2, §10, V-14, V-24; F-124, F-125, F-127): je Schuss
 * ein `article` „Schuss <Meldungs-Id>“ — aus der Warteschlange bzw. aus dem Lesemodell „eigene Meldungen“, auch ohne Netz
 * aus den Daten des Jagdtags — mit `button` „Ergänzen“. Er öffnet die Ansicht „Ergänzen“ (`Ergaenzen.tsx`) mit
 * „Fluchtrichtung“, „Pirschzeichen“, „Schussausgang“ und „Anschuss“. Ein übertragener, nicht stornierter Schuss (er steht
 * in den eigenen Meldungen) hat daneben `button` „Entfernen“: Stornieren nach dem Rückgängig-Fenster mit Grund
 * (`dialog` „Entfernen“, `textbox` „Grund“, `button` „Speichern“; schuss/04). Die Meldung bleibt erhalten; der Eintrag
 * zeigt danach `storniert`.
 */
const storniert = new Set<string>();
const beobachter = new Set<() => void>();

function stornoMerken(schuss: string) {
  storniert.add(schuss);
  for (const b of beobachter) b();
}

function useStorniert(schuss: string): boolean {
  return useSyncExternalStore((b) => { beobachter.add(b); return () => beobachter.delete(b); }, () => storniert.has(schuss));
}

export function SchussAktionen({ schuss, stornierbar }: { schuss: string; stornierbar: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const ausSchuss = useDrueckjagdDesSchusses(schuss);
  const dj = id ?? ausSchuss;
  const istStorniert = useStorniert(schuss);
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState("");
  const [abweisung, setAbweisung] = useState<string | null>(null);

  function schliessen() {
    setOffen(false); setGrund(""); setAbweisung(null);
  }

  async function stornieren() {
    setAbweisung(null);
    const a = await befehl("SchussStornieren", { Schuss: schuss, ...(grund.trim() ? { Grund: grund.trim() } : {}) });
    if (!a.ok) { setAbweisung(abweisungsgrund(a)); return; }
    stornoMerken(schuss);
    schliessen();
  }

  if (istStorniert) return <span className="eintrag__meta eintrag__status">storniert</span>;
  return (
    <div className={`raster ${stornierbar ? "raster--2" : "raster--1"}`}>
      <Kachel symbol="ergaenzen" begriff="Ergänzen" art="kompakt" isDisabled={!dj}
        onPress={() => navigate(`/drueckjagd/${dj}/ergaenzen/${encodeURIComponent(schuss)}`)} />
      {stornierbar ? <Kachel symbol="schliessen" begriff="Entfernen" art="kompakt" onPress={() => setOffen(true)} /> : null}
      <Dialog name="Entfernen" symbol="schliessen" offen={offen} onSchliessen={schliessen}>
        <div className="formular">
          <Textfeld label="Grund" wert={grund} onChange={setGrund} autoFocus />
          <Abweisung grund={abweisung} />
          <div className="knopfreihe">
            <Knopf onPress={() => void stornieren()}>Speichern</Knopf>
            <Knopf art="zweit" onPress={schliessen}>Abbrechen</Knopf>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function useSchussmeldungen(): EigeneMeldung[] {
  return useEigeneMeldungen().map((m) => ({
    meldungsId: String(m["Schuss"] ?? m._id), titel: `Schuss ${String(m["Schuss"] ?? m._id)}`,
    zeitpunkt: m["Zeitpunkt"] instanceof Date ? m["Zeitpunkt"] : null,
  }));
}

const SCHUSS_TITEL = /^Schuss (\S+)$/;

function Aktionen({ schuss }: { schuss: string }) {
  const eigene = useEigeneMeldungen();
  return <SchussAktionen schuss={schuss} stornierbar={eigene.some((m) => String(m["Schuss"] ?? m._id) === schuss)} />;
}

meldungszusatzRegistrieren({
  useEintraege: useSchussmeldungen,
  aktionen(titel) {
    const t = SCHUSS_TITEL.exec(titel);
    return t ? <Aktionen schuss={t[1]!} /> : null;
  },
});
